import { SyteProductType } from 'src/services/src/service/types/constants/syte-product-types';
import { SimilarItemsGeneralSettings } from 'src/services/src/service/similar-items/similarItems';
import { ShopTheLookGeneralSettings } from 'src/services/src/service/shop-the-look/shop-the-look';
import { PersonalizationGeneralSettings } from 'src/services/src/service/personalization/personalization';
import { ViewConfigValue } from './types';

export interface ViewConfigAdapterArgs {
  shopId: number;
  variantId: string;
}

export interface ViewConfigAdapter {
  resultsCountOptions: number[];
  get: (args: ViewConfigAdapterArgs) => Promise<ViewConfigValue>;
  update: (args: ViewConfigAdapterArgs & { value: Partial<ViewConfigValue> }) => Promise<void>;
}

const similarItemsService = new SimilarItemsGeneralSettings();
const shopTheLookService = new ShopTheLookGeneralSettings();
const personalizationService = new PersonalizationGeneralSettings();

// Each surface stores results-count/add-to-cart on its own general-settings document with its
// own required fields (see e.g. `ShopTheLookGeneralSettingsRequestPatch` on the backend), so an
// update always fetches the current document first and sends it back with only the two fields
// this editor touches changed, rather than trying to model each surface's whole shape here.
const VIEW_CONFIG_ADAPTERS: Partial<Record<SyteProductType, ViewConfigAdapter>> = {
  [SyteProductType.SimilarItems]: {
    resultsCountOptions: [5, 8, 12],
    get: ({ shopId, variantId }) =>
      similarItemsService.get({ shopId, variantId }).then(settings => ({
        numberOfResultsToDisplay: settings.numberOfResultsToDisplay,
        addToCartActive: settings.addToCart.active,
      })),
    update: ({ shopId, variantId, value }) =>
      similarItemsService.get({ shopId, variantId }).then(current =>
        similarItemsService
          .update({
            shopId,
            variantId,
            ...current,
            numberOfResultsToDisplay:
              value.numberOfResultsToDisplay ?? current.numberOfResultsToDisplay,
            addToCart: {
              ...current.addToCart,
              active: value.addToCartActive ?? current.addToCart.active,
            },
          })
          .then(() => undefined)
      ),
  },
  [SyteProductType.ShopTheLook]: {
    resultsCountOptions: [5, 8, 12],
    get: ({ shopId, variantId }) =>
      shopTheLookService.get({ shopId, variantId }).then(settings => ({
        numberOfResultsToDisplay: settings.numberOfResultsToDisplay,
        addToCartActive: settings.addToCart.active,
      })),
    update: ({ shopId, variantId, value }) =>
      shopTheLookService.get({ shopId, variantId }).then(current =>
        shopTheLookService
          .update({
            shopId,
            variantId,
            ...current,
            numberOfResultsToDisplay:
              value.numberOfResultsToDisplay ?? current.numberOfResultsToDisplay,
            addToCart: {
              ...current.addToCart,
              active: value.addToCartActive ?? current.addToCart.active,
            },
          })
          .then(() => undefined)
      ),
  },
  [SyteProductType.Personalization]: {
    resultsCountOptions: [5, 8, 12],
    get: ({ shopId, variantId }) =>
      personalizationService.get({ shopId, variantId }).then(settings => ({
        numberOfResultsToDisplay: settings.numberOfResultsToDisplay,
        addToCartActive: settings.addToCart.active,
      })),
    update: ({ shopId, variantId, value }) =>
      personalizationService.get({ shopId, variantId }).then(current =>
        personalizationService
          .update({
            shopId,
            variantId,
            ...current,
            numberOfResultsToDisplay:
              value.numberOfResultsToDisplay ?? current.numberOfResultsToDisplay,
            addToCart: {
              ...current.addToCart,
              active: value.addToCartActive ?? current.addToCart.active,
            },
          })
          .then(() => undefined)
      ),
  },
};

export function getViewConfigAdapter(surface: SyteProductType): ViewConfigAdapter | undefined {
  return VIEW_CONFIG_ADAPTERS[surface];
}
